import {ChevronLeft, ChevronRight, Briefcase} from 'lucide-react'
import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import { Link } from 'react-router-dom'
import JobCard from './JobCard'

interface Vaga {
  _id: string
  cargo: string
  descricao: string
  localizacao: string
  valorHora?: number
  tipoVaga?: string
  regime?: string
  dataCriacao?: string
  createdAt?: string
  fotoUsuario?: string
  bannerUsuario?: string
  nomeEstabelecimento?: string
  estabelecimentoNome?: string
  tipoContrato?: string
  userId?: string
}

function FeaturedJobs() {
  const [vagas, setVagas] = useState<Vaga[]>([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const vagasPorPagina = 3

  useEffect(() => {
    const fetchVagas = async () => {
      try {
        const { list } = await lumi.entities.vagas.list({
          sort: { createdAt: -1 },
          limit: 12
        })
        setVagas(list as Vaga[])
      } catch (err) {
        console.error('Erro ao buscar vagas:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchVagas()
  }, [])

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev + vagasPorPagina >= vagas.length ? 0 : prev + vagasPorPagina
    )
  }

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? Math.max(0, vagas.length - vagasPorPagina) : Math.max(0, prev - vagasPorPagina)
    )
  }

  const vagasVisiveis = vagas.slice(currentIndex, currentIndex + vagasPorPagina)

  if (loading || vagas.length === 0) {
    return null
  }

  return (
    <section id="vagas" className="py-20 bg-gradient-to-b from-[#2C2C2C] to-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Vagas em <span className="text-[#E53935]">Destaque</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Oportunidades exclusivas em restaurantes e churrascarias de Milão
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-4 border-[#E53935] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : vagas.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase size={48} className="text-[#5D4037] mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Nenhuma vaga disponível no momento.</p>
            <p className="text-gray-500 text-sm mt-2">Volte em breve para novas oportunidades!</p>
          </div>
        ) : (
          <>
            {/* Carousel Controls */}
            {vagas.length > vagasPorPagina && (
              <div className="flex justify-between items-center mb-8">
                <button
                  onClick={prevSlide}
                  className="bg-[#5D4037] p-3 rounded-full shadow-lg hover:shadow-xl hover:bg-[#6D4C41] text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2 border-[#8D6E63]"
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft size={24} />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: Math.ceil(vagas.length / vagasPorPagina) }).map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-2 rounded-full transition-all ${
                        Math.floor(currentIndex / vagasPorPagina) === idx
                          ? 'w-8 bg-[#E53935]'
                          : 'w-2 bg-[#5D4037]'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  className="bg-[#5D4037] p-3 rounded-full shadow-lg hover:shadow-xl hover:bg-[#6D4C41] text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2 border-[#8D6E63]"
                  disabled={currentIndex + vagasPorPagina >= vagas.length}
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            )}

            {/* Cards */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,280px),300px))] justify-center gap-5">
              {vagasVisiveis.map((vaga) => (
                <JobCard key={vaga._id}
                  cargo={vaga.cargo}
                  estabelecimento={vaga.estabelecimentoNome || vaga.nomeEstabelecimento}
                  descricao={vaga.descricao} localizacao={vaga.localizacao}
                  valorHora={vaga.valorHora} foto={vaga.fotoUsuario} capa={vaga.bannerUsuario}
                  data={vaga.createdAt || vaga.dataCriacao} contrato={vaga.tipoContrato || vaga.tipoVaga}
                  href="/vagas" />
              ))}
            </div>
          </>
        )}

        {/* Ver todas */}
        <div className="text-center mt-12">
          <Link
            to="/vagas"
            className="inline-block bg-[#E53935] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#C62828] transition-all hover:shadow-xl hover:shadow-[#E53935]/50"
          >
            Ver todas as vagas {vagas.length > 0 ? `(${vagas.length})` : ''}
          </Link>
        </div>
      </div>
    </section>
  )
}

export default FeaturedJobs
